// GIF → 가로 스프라이트 스트립(webp) 변환 — AR 오버레이용(FR-AR-08).
// WebGL 텍스처는 GIF 의 첫 프레임만 읽어 애니가 안 나온다 → 업로드 시점에 프레임을 풀어
// 기존 스트립 파이프라인(가로 정사각형 N프레임 + fps)으로 바꿔 넣는다(재생 경로 무수정).
// 디코더는 브라우저 내장 ImageDecoder(WebCodecs) — 운영자 환경(데스크톱 크롬/엣지) 기준.

const MAX_EDGE = 4096; // 슬롯 규격(maxLongEdgePx)과 일치 — 스트립 가로 상한
const MAX_FRAME_H = 512; // 프레임 높이 상한(과대 GIF 다운스케일)

// TS lib.dom 에 아직 없는 WebCodecs ImageDecoder 최소 타입
interface DecodedFrame {
  image: {
    duration: number | null; // microseconds
    displayWidth: number;
    displayHeight: number;
    close: () => void;
  } & CanvasImageSource;
}
interface ImageDecoderLike {
  tracks: { ready: Promise<unknown>; selectedTrack: { frameCount: number } | null };
  decode(opts: { frameIndex: number }): Promise<DecodedFrame>;
  close: () => void;
}
declare const ImageDecoder: (new (init: { data: ArrayBuffer; type: string }) => ImageDecoderLike) | undefined;

export interface GifStrip {
  blob: Blob; // webp 가로 스트립
  frames: number;
  fps: number; // GIF 원 타이밍 기반(재생부에서 시각안전 클램프)
  width: number;
  height: number;
}

export function gifConvertSupported(): boolean {
  return typeof ImageDecoder !== "undefined";
}

/** GIF 파일 → 스트립. 프레임이 많으면 균등 샘플링(스트립 가로 4096px 제한). */
export async function gifToStrip(file: File): Promise<GifStrip> {
  if (typeof ImageDecoder === "undefined") {
    throw new Error("이 브라우저는 GIF 변환을 지원하지 않아요(크롬/엣지에서 해주세요).");
  }
  const dec = new ImageDecoder({ data: await file.arrayBuffer(), type: "image/gif" });
  try {
    await dec.tracks.ready;
    const total = dec.tracks.selectedTrack?.frameCount ?? 0;
    if (total < 2) throw new Error("움직이는 GIF가 아니에요(프레임 1장).");

    // 프레임 셀은 **정사각**(재생부 프레임 감지 규격 = 가로÷세로). 원본은 셀 안에 비율 유지로 중앙 배치.
    const first = await dec.decode({ frameIndex: 0 });
    const srcW = first.image.displayWidth;
    const srcH = first.image.displayHeight;
    const cell = Math.min(MAX_FRAME_H, Math.max(srcW, srcH));
    const maxFrames = Math.max(2, Math.floor(MAX_EDGE / cell));
    const frames = Math.min(total, maxFrames);
    // 균등 샘플 인덱스(전 구간을 훑어 동작 보존)
    const indices = Array.from({ length: frames }, (_, i) => Math.round((i * (total - 1)) / Math.max(1, frames - 1)));

    const canvas = document.createElement("canvas");
    canvas.width = cell * frames;
    canvas.height = cell;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("캔버스를 만들 수 없어요.");

    // 셀 내 배치(비율 유지·중앙)
    const fit = Math.min(cell / srcW, cell / srcH);
    const dw = Math.round(srcW * fit);
    const dh = Math.round(srcH * fit);
    const dx0 = Math.round((cell - dw) / 2);
    const dy = Math.round((cell - dh) / 2);

    let totalDurUs = 0;
    for (let i = 0; i < indices.length; i++) {
      const idx = indices[i]!;
      const { image } = idx === 0 ? first : await dec.decode({ frameIndex: idx });
      ctx.drawImage(image, i * cell + dx0, dy, dw, dh);
      totalDurUs += image.duration ?? 100_000; // 미지정 프레임은 0.1s 로 간주
      if (idx !== 0) image.close();
    }
    first.image.close();

    // 샘플된 프레임 평균 지속시간 → fps (비정상값은 8fps 폴백)
    const avgSec = totalDurUs / indices.length / 1_000_000;
    const fps = Number.isFinite(avgSec) && avgSec > 0 ? Math.min(30, Math.max(0.2, 1 / avgSec)) : 8;

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/webp", 0.9));
    if (!blob) throw new Error("webp 변환에 실패했어요.");
    return { blob, frames, fps: Math.round(fps * 10) / 10, width: canvas.width, height: canvas.height };
  } finally {
    dec.close();
  }
}
